/**
 * HeaderBar — headless paired-device header.
 *
 * Headless: no `browser.*`, no `chrome.*`, no storage hooks, no i18n
 * helpers. All data arrives as props; the host resolves storage
 * reads + i18n labels and passes them in. Mirrors the conventions
 * established by the other headless feature wrappers in this package
 * (`SettingsPanel`, `SidePanelNotificationList`).
 *
 * Layout (matches Figma node 1016:1782):
 *   [StatusDot + sr-only status text] [device name]    [⚙ settings]
 *
 * The Figma frame only shows `[device name | settings]` — no
 * connection indicator and no unread badge — but a connection
 * affordance is load-bearing UX (users need to know if mirroring
 * is live), so we add a small `<StatusDot>` to the LEFT of the
 * device name. Treat the dot as a Figma omission rather than a
 * deliberate drop. The unread badge from the legacy StatusBar is
 * intentionally NOT carried over: the design clearly didn't
 * include it, and unread visibility belongs to the notification
 * list itself, not the header.
 *
 * Connection state has THREE values (a binary
 * `connected ? "Connected" : "Disconnected"` is a regression — a
 * paired device that loses SSE for a moment should announce
 * "Reconnecting…", not "Disconnected"):
 *   - `sseConnected === true`                       → labels.statusConnected (regardless of `paired`)
 *   - `sseConnected === false && paired === true`    → labels.statusReconnecting
 *   - `sseConnected === false && paired === false`   → labels.statusDisconnected
 *   - `sseConnected === undefined`                   → empty (loading window)
 *   - `sseConnected === false && paired === undefined` → empty (loading window)
 *
 * The `sseConnected === true` clause runs first, so a paired-but-
 * pre-storage-load tab still announces "Connected" once SSE is up
 * even before `paired` resolves — the legacy StatusBar had the
 * same precedence.
 *
 * Both the label and the dot's tone are derived from ONE `ConnectionState`
 * value below, deliberately. They used to be independent: the label carried
 * the three-state truth while the dot was hardcoded green, so the header
 * showed a healthy indicator through an outage that the `sr-only` text was
 * correctly reporting. Two renderings of one state is how that happens; one
 * source is how it stops.
 *
 * Accessibility:
 *   - The visible StatusDot is rendered DECORATIVE (no `label`),
 *     so it carries `aria-hidden="true"` and screen readers skip
 *     it. The connection status text is provided as a sr-only
 *     `<span>` inside a `role="status"` region (implicit live
 *     region with politeness level "polite") — when the three-
 *     state computation produces a new value (SSE drops or
 *     recovers while the panel is open), assistive tech announces
 *     the change. Live-region text is the announcement source
 *     because `role="img" + aria-label` is a static semantic that
 *     does NOT re-announce on label changes.
 *   - During the host's initial storage-load race, the live-region
 *     text is empty, so assistive tech doesn't announce a stale
 *     state. The visual StatusDot still renders to keep the layout
 *     stable, in the `neutral` tone: there is no "unknown" colour, and
 *     between the two candidates a quiet grey withholds a claim while
 *     green would assert the one thing that has to be earned. The
 *     window is a single storage read, so it isn't perceptible.
 *   - The `<header>` element maps to the `banner` ARIA landmark,
 *     and gets `aria-label={labels.banner}` ("Reflecto") so screen
 *     readers navigating by landmark land on a named banner instead
 *     of an anonymous one.
 *
 * `onOpenSettings` is optional — when omitted the IconButton is NOT
 * rendered at all. A no-op interactive button is worse than no
 * button (keyboard users would tab to a focusable element that
 * does nothing).
 */

import { Settings01 } from "@untitledui/icons";
import type { ReactNode } from "react";
import { IconButton } from "../IconButton";
import { StatusDot, type StatusDotTone } from "../StatusDot";
import styles from "./HeaderBar.module.css";

/** The single derived connection state both the label and the dot render from. */
type ConnectionState = "connected" | "reconnecting" | "disconnected" | "loading";

/** `null` = say nothing (the storage-load window). */
const CONNECTION_LABEL_KEY: Record<ConnectionState, keyof HeaderBarLabels | null> = {
  connected: "statusConnected",
  reconnecting: "statusReconnecting",
  disconnected: "statusDisconnected",
  loading: null,
};

/**
 * Only `reconnecting` pulses. It is the one state that is actively trying to
 * recover, and with the label kept `sr-only` the motion is what separates it
 * from `disconnected` — otherwise the two differ by hue alone.
 */
const CONNECTION_DOT: Record<ConnectionState, { tone: StatusDotTone; pulse: boolean }> = {
  connected: { tone: "positive", pulse: false },
  reconnecting: { tone: "warning", pulse: true },
  disconnected: { tone: "neutral", pulse: false },
  loading: { tone: "neutral", pulse: false },
};

/**
 * i18n bag for every visible/announced string in the header.
 *
 * `banner` is the ARIA-landmark name (typically the product name,
 * e.g. "Reflecto"). The three `status*` strings feed the sr-only
 * live region so AT announces the right state on SSE flap.
 */
export interface HeaderBarLabels {
  /** Banner aria-label — e.g. "Reflecto". */
  banner: string;
  /** Connection status announcements (used in the sr-only live region). */
  statusConnected: string;
  statusReconnecting: string;
  statusDisconnected: string;
  /** IconButton aria-label for the settings button. */
  openSettings: string;
}

export interface HeaderBarProps {
  /**
   * Display name for the paired device. Hosts pass the resolved
   * label (e.g. extension passes `androidDeviceName ?? t("androidDevice")`).
   * Empty string is treated as "no name available" and the device-name
   * span renders empty (CSS keeps the layout stable).
   */
  deviceLabel: string;

  /**
   * Pairing state from storage. Drives the three-state connection
   * label together with `sseConnected`. `undefined` during the initial
   * storage-load race — leaves the live region empty so AT doesn't
   * announce a stale state.
   */
  paired?: boolean;

  /**
   * SSE connection state from storage. Drives the three-state
   * connection label together with `paired`. `undefined` during the
   * initial storage-load race.
   */
  sseConnected?: boolean;

  /**
   * Click handler for the settings IconButton. When omitted, the
   * IconButton is NOT rendered — a no-op interactive button is worse
   * than no button (keyboard users would tab to a focusable element
   * that does nothing).
   */
  onOpenSettings?: () => void;

  /**
   * Optional content rendered to the LEFT of the settings IconButton in
   * the header's trailing cluster. Hosts pass a `<PublicModeToggle>` (or
   * any other right-aligned interactive element) here. When omitted no
   * extra slot renders — keeps the header layout identical for hosts
   * that don't surface the affordance.
   */
  trailing?: ReactNode;

  /** i18n bag for every visible string. */
  labels: HeaderBarLabels;
}

export function HeaderBar({
  deviceLabel,
  paired,
  sseConnected,
  onOpenSettings,
  trailing,
  labels,
}: HeaderBarProps) {
  // ONE derivation, two renderings. See the note in the module doc: the label
  // and the dot must never be able to disagree.
  const connection: ConnectionState =
    sseConnected === true
      ? "connected"
      : sseConnected === false && paired === true
        ? "reconnecting"
        : sseConnected === false && paired === false
          ? "disconnected"
          : "loading";

  // Empty during the storage-load race so the live region doesn't flash a
  // stale state on first render.
  const labelKey = CONNECTION_LABEL_KEY[connection];
  const connectionLabel = labelKey ? labels[labelKey] : "";
  const { tone, pulse } = CONNECTION_DOT[connection];

  return (
    // <header> at the top level of a popup/sidepanel/page implicitly
    // maps to the `banner` ARIA landmark, which DOES support
    // `aria-label`. Biome's `useAriaPropsSupportedByRole` rule can't
    // verify the implicit role from static analysis (it would need to
    // know the sectioning ancestry) and emits a false positive. The
    // alternatives all break other Biome a11y rules:
    //   - explicit `role="banner"` → noInteractiveElementToNoninteractiveRole
    //   - `<div role="banner">`     → useSemanticElements
    // Targeted ignore is the only way to ship a named banner landmark.
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: <header> at popup root implicitly has role="banner", which supports aria-label
    <header className={styles.bar} aria-label={labels.banner}>
      <div className={styles.left}>
        {/* `role="status"` IS an implicit live region with politeness
         *  level "polite" AND `aria-atomic="true"` per the ARIA spec —
         *  no separate `aria-live="polite"` attribute needed (it
         *  would just duplicate the role's contract). The implicit
         *  `aria-atomic` means the entire region is announced on
         *  change rather than just modified nodes; harmless here
         *  because StatusDot is decorative (`aria-hidden="true"`)
         *  so the only contributor to the accessible name is the
         *  sr-only sibling's text content. The role also gives the
         *  region a stable test handle (`getByRole("status")`) that
         *  won't break if a future sibling adds another polite
         *  live region. */}
        <span className={styles.connectionStatus} role="status">
          <StatusDot tone={tone} pulse={pulse} />
          <span className={styles.srOnly}>{connectionLabel}</span>
        </span>
        <span className={styles.deviceName} title={deviceLabel}>
          {deviceLabel}
        </span>
      </div>
      {(trailing || onOpenSettings) && (
        <div className={styles.trailing}>
          {trailing}
          {onOpenSettings && (
            <IconButton
              icon={Settings01}
              label={labels.openSettings}
              variant="default"
              size="m"
              onClick={onOpenSettings}
            />
          )}
        </div>
      )}
    </header>
  );
}
